// The claim table's reading of a bearer's claims, without a component: the
// bands by area, one group per stance and core ability with what its claims
// state counted, and the groups whose ability is on no curated list set
// apart.

import {
  CapabilityClaim,
  ClaimPart,
  EntityRef,
  FactSource,
  Stance,
} from "../../types/productPlane";

// What a bearer has comes first, then what it lets a host have, then the
// stated gaps: what it requires and what it lacks
const STANCE_ORDER: Record<Stance, number> = {
  has: 0,
  enables: 1,
  requires: 2,
  lacks: 3,
};

// The stances that look past the bearer itself: what it lets a host have
// and what it needs. Their claims share the last band, whatever the area.
const HOST_STANCES: ReadonlySet<Stance> = new Set<Stance>([
  "enables",
  "requires",
]);

// What the last band reads
export const HOST_BAND_LABEL = "Enables in the host · Requires";

// The maturity no source states: it is no maturity to show
const UNKNOWN_MATURITY = "unknown";

// The maturities, the furthest first: a group shows the furthest any of its
// claims states
const MATURITY_ORDER: readonly string[] = [
  "in_service",
  "demonstrated",
  "in_development",
  "planned",
];

// A part with the number of the group's claims that state it
export interface CountedPart {
  part: ClaimPart;
  count: number;
}

// Grade words with the number of the group's claims that use them
export interface CountedWords {
  words: string;
  count: number;
}

// The claims of one stance on one core ability
export interface ClaimGroup {
  key: string;
  stance: Stance;
  // The core ability; one in the normal case
  abilities: ClaimPart[];
  // What the ability acts on, of any of the claims
  objects: ClaimPart[];
  // Whether the ability is on a curated list; where it is not, the group
  // is one of the band's unlisted
  listed: boolean;
  // In the order given
  claims: CapabilityClaim[];
  // What the claims state, the most stated first
  conditions: CountedPart[];
  modes: CountedPart[];
  grades: CountedWords[];
  // The furthest maturity a claim states; null when none states one
  maturity: string | null;
  // The documents behind the claims, each once
  sources: FactSource[];
}

export interface ClaimBand {
  key: string;
  effectArea: EntityRef | null;
  functionArea: EntityRef | null;
  // What the band reads instead of its areas
  label?: string;
  // The groups whose ability is on a curated list
  groups: ClaimGroup[];
  // The groups whose ability is in an article's own words
  unlisted: ClaimGroup[];
}

// A stance the table does not know yet comes last
const stanceRank = (stance: Stance): number =>
  STANCE_ORDER[stance] ?? Object.keys(STANCE_ORDER).length;

// The maturity a claim states; null for none and for "unknown"
export const statedMaturity = (claim: CapabilityClaim): string | null =>
  claim.maturity && claim.maturity !== UNKNOWN_MATURITY ? claim.maturity : null;

// A maturity the order does not know comes after those it does
const maturityRank = (maturity: string): number => {
  const rank = MATURITY_ORDER.indexOf(maturity);
  return rank === -1 ? MATURITY_ORDER.length : rank;
};

const furthestMaturity = (claims: CapabilityClaim[]): string | null =>
  claims
    .map(statedMaturity)
    .filter((maturity): maturity is string => maturity !== null)
    .sort((left, right) => maturityRank(left) - maturityRank(right))[0] ?? null;

// Array.prototype.sort is stable: equal counts keep the order of appearance
const mostFirst = <T extends { count: number }>(counted: T[]): T[] =>
  counted.sort((left, right) => right.count - left.count);

// The parts of one role over the claims, each with the claims that state it
const countedParts = (
  claims: CapabilityClaim[],
  partsOf: (claim: CapabilityClaim) => ClaimPart[],
): CountedPart[] => {
  const byId = new Map<string, CountedPart>();
  for (const claim of claims) {
    const stated = new Map(partsOf(claim).map((part) => [part.part.id, part]));
    stated.forEach((part, id) => {
      const known = byId.get(id);
      if (known) {
        known.count += 1;
      } else {
        byId.set(id, { part, count: 1 });
      }
    });
  }
  return mostFirst(Array.from(byId.values()));
};

const countedGrades = (claims: CapabilityClaim[]): CountedWords[] => {
  const byWords = new Map<string, CountedWords>();
  for (const claim of claims) {
    new Set(claim.grade_words).forEach((words) => {
      const known = byWords.get(words);
      if (known) {
        known.count += 1;
      } else {
        byWords.set(words, { words, count: 1 });
      }
    });
  }
  return mostFirst(Array.from(byWords.values()));
};

// The documents behind the claims, each once, as the first claim marks it
const sourcesOf = (claims: CapabilityClaim[]): FactSource[] => {
  const byDocument = new Map<string, FactSource>();
  for (const claim of claims) {
    for (const source of claim.sources) {
      if (!byDocument.has(source.document_id)) {
        byDocument.set(source.document_id, source);
      }
    }
  }
  return Array.from(byDocument.values());
};

// What a group is called: its abilities
export const groupName = (group: Pick<ClaimGroup, "abilities">): string =>
  group.abilities.map((ability) => ability.part.name ?? ability.part.id).join(" · ");

export const countedClaims = (count: number): string =>
  `${count} claim${count === 1 ? "" : "s"}`;

const groupOf = (key: string, claims: CapabilityClaim[]): ClaimGroup => {
  const [{ stance, capability }] = claims;
  return {
    key,
    stance,
    abilities: capability.abilities,
    objects: countedParts(claims, (claim) => claim.capability.objects).map(
      (counted) => counted.part,
    ),
    listed:
      capability.abilities.length > 0 &&
      capability.abilities.every((ability) => ability.curated),
    claims,
    conditions: countedParts(claims, (claim) => claim.capability.conditions),
    modes: countedParts(claims, (claim) => claim.capability.modes),
    grades: countedGrades(claims),
    maturity: furthestMaturity(claims),
    sources: sourcesOf(claims),
  };
};

// The groups of a band: by stance, then the most claims, then the name
const byWeight = (left: ClaimGroup, right: ClaimGroup): number =>
  stanceRank(left.stance) - stanceRank(right.stance) ||
  right.claims.length - left.claims.length ||
  groupName(left).localeCompare(groupName(right));

interface OpenBand {
  key: string;
  effectArea: EntityRef | null;
  functionArea: EntityRef | null;
  label?: string;
  claims: Map<string, CapabilityClaim[]>;
}

const closed = (band: OpenBand): ClaimBand => {
  const groups = Array.from(band.claims.entries())
    .map(([key, claims]) => groupOf(`${band.key}#${key}`, claims))
    .sort(byWeight);
  return {
    key: band.key,
    effectArea: band.effectArea,
    functionArea: band.functionArea,
    label: band.label,
    groups: groups.filter((group) => group.listed),
    unlisted: groups.filter((group) => !group.listed),
  };
};

// What makes two abilities one core. An ability on a curated list is its
// row; one in an article's own words is its wording, whatever the case: the
// same words come as several parts where the articles were not brought
// together.
const coreKey = (ability: ClaimPart): string =>
  ability.curated
    ? ability.part.id
    : `words:${(ability.part.name ?? ability.part.id).trim().toLowerCase()}`;

/**
 * The core a claim is grouped under: its abilities, a curated one by its id
 * and any other by its words, so that the many uncurated parts of one name
 * are one core. A capability that states no ability is a core of its own.
 * The product page groups by it, and the comparison makes a row of it.
 */
export const coreOf = (claim: CapabilityClaim): string =>
  claim.capability.abilities.map(coreKey).join("+") ||
  claim.capability.capability.id;

// The first stance of a band: that of its first group, listed or not
const bandRank = (band: ClaimBand): number =>
  Math.min(
    ...[...band.groups, ...band.unlisted].map((group) => stanceRank(group.stance)),
  );

// The claims as bands by effect area and function area, and after them one
// band for every claim the bearer enables in a host or requires. Within a
// band the claims of one stance on one core ability are one group; the area
// bands run by their first stance, so a band that holds only gaps comes
// after the others.
export const groupClaims = (claims: CapabilityClaim[]): ClaimBand[] => {
  const bands = new Map<string, OpenBand>();
  const host: OpenBand = {
    key: "host",
    effectArea: null,
    functionArea: null,
    label: HOST_BAND_LABEL,
    claims: new Map(),
  };
  for (const claim of claims) {
    const { effect_area: effectArea, function_area: functionArea } =
      claim.capability;
    const bandKey = `${effectArea?.id ?? ""}|${functionArea?.id ?? ""}`;
    let band = host;
    if (!HOST_STANCES.has(claim.stance)) {
      band = bands.get(bandKey) ?? {
        key: bandKey,
        effectArea,
        functionArea,
        claims: new Map(),
      };
      bands.set(bandKey, band);
    }
    const key = `${claim.stance}:${coreOf(claim)}`;
    band.claims.set(key, [...(band.claims.get(key) ?? []), claim]);
  }
  const ordered = Array.from(bands.values()).map(closed);
  ordered.sort((left, right) => bandRank(left) - bandRank(right));
  return host.claims.size > 0 ? [...ordered, closed(host)] : ordered;
};
